const ERUDA_SCRIPT_ID = 'spotty-eruda-script'
const ERUDA_CDN_URL = 'https://cdn.jsdelivr.net/npm/eruda'
const ERUDA_LOAD_TIMEOUT_MS = 2000

interface ErudaApi {
  init: () => void
}

type ErudaWindow = Window & {
  eruda?: ErudaApi
  __spottyErudaBootstrapped?: boolean
  __spottyErudaLoading?: Promise<void>
}

const withTimeout = (promise: Promise<void>, timeoutMs: number): Promise<void> =>
  Promise.race([
    promise,
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, timeoutMs)
    }),
  ])

const initEruda = (win: ErudaWindow) => {
  if (win.__spottyErudaBootstrapped || !win.eruda) {
    return
  }

  win.eruda.init()
  win.__spottyErudaBootstrapped = true
}

const loadEruda = (win: ErudaWindow): Promise<void> => {
  if (win.__spottyErudaLoading) {
    return win.__spottyErudaLoading
  }

  win.__spottyErudaLoading = new Promise((resolve) => {
    if (win.eruda) {
      initEruda(win)
      resolve()
      return
    }

    const existingScript = document.getElementById(ERUDA_SCRIPT_ID) as HTMLScriptElement | null
    if (existingScript) {
      existingScript.addEventListener(
        'load',
        () => {
          initEruda(win)
          resolve()
        },
        { once: true }
      )
      existingScript.addEventListener(
        'error',
        () => {
          console.warn('[devtools] Failed to load Eruda')
          resolve()
        },
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.id = ERUDA_SCRIPT_ID
    script.src = ERUDA_CDN_URL
    script.async = true
    script.addEventListener('load', () => {
      initEruda(win)
      resolve()
    })
    script.addEventListener('error', () => {
      console.warn('[devtools] Failed to load Eruda')
      resolve()
    })

    document.head.appendChild(script)
  })

  return win.__spottyErudaLoading
}

/**
 * Decide whether to bootstrap Eruda in the current environment. Exposed for
 * unit testing; gating is intentionally permissive on `dev.spotty.one` where
 * QA engineers need console access on mobile — the deploy contains no prod
 * secrets and surfaces only dev-environment session data (Phantom ephemeral
 * keys, wallet addresses) that has no value beyond the session itself.
 */
export function shouldBootstrapEruda(): boolean {
  if (typeof window === 'undefined') return false
  // A diagnostic reproduction should not load an extra DOM/console recorder.
  const params = new URLSearchParams(window.location.search)
  if (params.has('diag') && !params.has('debug')) return false
  if (import.meta.env.DEV) return true
  const { hostname, search } = window.location
  if (hostname === 'dev.spotty.one') return true
  try {
    if (new URLSearchParams(search).has('debug')) return true
  } catch {
    // URLSearchParams is available in every browser we support; guard only
    // for exotic embedded contexts.
  }
  return false
}

export async function bootstrapEruda(): Promise<void> {
  if (!shouldBootstrapEruda() || typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const win = window as ErudaWindow
  if (win.eruda) {
    initEruda(win)
    return
  }

  await withTimeout(loadEruda(win), ERUDA_LOAD_TIMEOUT_MS)
}
